import {
  Component, Input, Output, OnChanges, OnInit, ChangeDetectorRef,
  OnDestroy, SimpleChanges, EventEmitter, AfterContentChecked
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, FormArray, Validators } from '@angular/forms';
import { NodeAPIEntityService } from './../services/node.entity.service';
import {
  TogetherAuctionDIPEntityType,
  TogetherAuctionDIPEntityOperation
} from './../models/enums';
import { TogetherAuctionDIPSecurityDetails, TogetherBaseAddress } from './../models/api.models';
import { NodeAPIService } from './../services/node.api.service';
import { ExceptionService } from '../services/exception.service';
import * as _ from 'lodash-es';
import moment from 'moment';

/**
 *
 * @export
 * @class TogetherAuctionDIPAdditionalDetailsSecurityComponent
 * @implements {OnChanges}
 * @implements {OnInit}
 */
@Component({
    selector: 'together-auction-dip-additional-details-security',
    templateUrl: './dip.additional_details.security.component.html',
    standalone: false
})
export class TogetherAuctionDIPAdditionalDetailsSecurityComponent implements OnChanges, OnInit, OnDestroy {
  @Input() additionalSecurityForm: UntypedFormGroup = this.fb.group({});
  @Input() security: TogetherAuctionDIPSecurityDetails = new TogetherAuctionDIPSecurityDetails();
  @Input() incoming_case_id: string;
  @Input() do_save: boolean;
  @Input() address: TogetherBaseAddress = new TogetherBaseAddress();
  @Output() has_saved: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() is_loading: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() form_out: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();
  @Input() parentFormGroup: UntypedFormGroup;

  security_response: object;
  addresslookup: UntypedFormGroup = this.fb.group({});
  tad_additional_security_address: UntypedFormGroup = new UntypedFormGroup({});
  tad_additional_security_address_postcode: UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_address_house_number_name: UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_address_street1: UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_address_town: UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_address_city: UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_address_county: UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_property_category: UntypedFormControl =
    new UntypedFormControl(null, [Validators.required]);
  tad_additional_security_property_type: UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_property_value: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    Validators.min(36400),
    Validators.minLength(5),
  ]);

  tad_additional_security_purchase_date_month: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    Validators.min(1),
    Validators.max(12),
  ]);
  tad_additional_security_purchase_date_year: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    Validators.min(1900),
    Validators.max(new Date().getFullYear()),
  ]);
  tad_additional_security_purchase_price: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    Validators.min(36400),
    Validators.minLength(5),
  ]);
  tad_additional_security_secured: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
  ]);
  tad_additional_security_secured_outstanding_mortgage_balance:
    UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_secured_outstanding_mortgage_lender:
    UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_secured_outstanding_mortgage_charge_type:
    UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_secured_outstanding_mortgage_other_loans_secured:
    UntypedFormControl = new UntypedFormControl(null, []);
  tad_additional_security_secured_outstanding_mortgage_loan_balance:
    UntypedFormControl = new UntypedFormControl(null, []);
  _isLoading: boolean = false;
  _additionalLoans: boolean = false;
  _additionaLoansOutstanding: boolean = false;
  _setLoading(loading: boolean) {
    this._isLoading = loading;
    this.is_loading.emit(loading);
  }
  get _showExistingMortgage() {
    return this.tad_additional_security_secured.value;
  }

  get _showAdditionalLoans() {
    return this.tad_additional_security_secured_outstanding_mortgage_other_loans_secured.value;
  }

  set _showExistingMortgage(newVal: boolean) {
    this.tad_additional_security_secured.setValue(newVal);
  }

  set _showAdditionalLoans(newVal: boolean) {
    this.tad_additional_security_secured_outstanding_mortgage_other_loans_secured
      .setValue(newVal);
  }

  _handleIncomingForm(form) {
    this.tad_additional_security_address = form;
    this.prepareForm();
  }

  reset(categoryValue: string) {
    this.tad_additional_security_property_category.valueChanges.subscribe(
      (selectedValue) => {
        this.tad_additional_security_property_type.setValidators([Validators.required]);
        this.prepareForm();
        if (selectedValue === '3') {
          this.tad_additional_security_property_type.setValue(37);
        } else {
          this.tad_additional_security_property_type.setValue(null);
        }
        this.tad_additional_security_property_type.markAsUntouched({ onlySelf: true });
      },
    );
  }

  /**
   *
   *
   * @memberof TogetherAuctionDIPAdditionalDetailsSecurityComponent
   */
  prepareSecurity() {
    if (this.security.caseSecurityRequest.SecurityRequest.AddressRequest.Id !== 0) {
      this.address = this.security.caseSecurityRequest.SecurityRequest.AddressRequest;
    }
    const purchaseMonth = this.tad_additional_security_purchase_date_month.value;
    const purchaseYear = this.tad_additional_security_purchase_date_year.value;

    this.security._purchaseDate = new Date(purchaseYear, purchaseMonth - 1, 1);
    this.security._propertyValue =
      this.tad_additional_security_property_value.value;
    this.security._purchasePrice =
      this.tad_additional_security_purchase_price.value;
    this.security.caseSecurityRequest.CaseSecurityAttributeRequest[0].PropertyCategoryId =
      this.tad_additional_security_property_category.value;
    this.security.caseSecurityRequest.CaseSecurityAttributeRequest[0].PropertyTypeId =
      this.tad_additional_security_property_type.value;

    if (_.isEmpty(this.tad_additional_security_address.controls) === false) {
      this.security.caseSecurityRequest.SecurityRequest.AddressRequest.HouseName =
        this.tad_additional_security_address.get('addressHouseName').value;
      this.security.caseSecurityRequest.SecurityRequest.AddressRequest.HouseNumber =
        this.tad_additional_security_address.get('addressHouseNumber').value;
      this.security.caseSecurityRequest.SecurityRequest.AddressRequest.Street1 =
        this.tad_additional_security_address.get('addressStreet').value;
      this.security.caseSecurityRequest.SecurityRequest.AddressRequest.PostTown =
        this.tad_additional_security_address.get('addressTownCity').value;
      this.security.caseSecurityRequest.SecurityRequest.AddressRequest.District =
        this.tad_additional_security_address.get('addressDistrict').value;
      this.security.caseSecurityRequest.SecurityRequest.AddressRequest.County =
        this.tad_additional_security_address.get('addressCounty').value;
      this.security.caseSecurityRequest.SecurityRequest.AddressRequest.Postcode =
        this.tad_additional_security_address.get('addressPostcode').value;
    }

    this.security.caseSecurityRequest.Address =
      this.security.caseSecurityRequest.SecurityRequest.AddressRequest;

    if (this.tad_additional_security_secured.value === 'true') {
      if (this.security.hasFirstCharge() === false) {
        this.security.initialiseFirstCharge();
      }
      this.security._firstChargeLender =
        this.tad_additional_security_secured_outstanding_mortgage_lender.value;
      this.security._firstChargeValue =
        this.tad_additional_security_secured_outstanding_mortgage_balance.value;
    }
    if (this.tad_additional_security_secured_outstanding_mortgage_other_loans_secured.value
      === 'true') {
      if (this.security.hasSecondCharge() === false) {
        this.security.initialiseSecondCharge();
      }
      this.security._secondChargeValue =
        this.tad_additional_security_secured_outstanding_mortgage_loan_balance.value;
    }

  }

  /**
*
*
* @memberof TogetherAuctionDIPAdditionalDetailsSecurityComponent
*/
  ngOnDestroy() {
    this.ref.detach();
  }

  /**
   *
   *
   * @memberof TogetherAuctionDIPAdditionalDetailsSecurityComponent
   */
  doSaveSecurity() {
    this._setLoading(true);
    this.security.caseSecurityRequest.ValuationRequest.splice(1);
    this.api.savePropertyDetails(this.incoming_case_id,
      this.security.caseSecurityRequest.SecurityId, this.security)
      .subscribe(
        (success) => {
          try {
            const obj = (success as any); //JSON.parse(success.text());
            this.security = new TogetherAuctionDIPSecurityDetails(obj);
            this._setLoading(false);
            this.has_saved.emit(true);
            // tslint:disable-next-line:variable-name
          } catch (JSONParseError) {
            this.has_saved.emit(false);
            this._setLoading(false);
            return;
          }
        },
        (error) => {
          this.exception.haveReceivedError(error);
          this.has_saved.emit(false);
          this._setLoading(false);
          return;
        },
      );
  }




  /**
   * Prepare the form.
   *
   * @memberof TogetherAuctionDIPAdditionalDetailsSecurityComponent
   */
  handleInit() {
    if (!_.isNil(this.security.caseSecurityRequest)) {
      const house = this.security.caseSecurityRequest.
        SecurityRequest.AddressRequest.HouseName
        || this.security.caseSecurityRequest.
          SecurityRequest.AddressRequest.HouseNumber;
      this.tad_additional_security_address_house_number_name.setValue(house);
      this.tad_additional_security_purchase_price.setValue(this.security._purchasePrice);
      this.tad_additional_security_property_value.setValue(this.security._propertyValue);

      const purchaseDate = this.security._purchaseDate;

      if (_.isNil(purchaseDate) === false) {

        this.tad_additional_security_purchase_date_month.
          setValue(purchaseDate.getMonth() + 1);
        this.tad_additional_security_purchase_date_year.
          setValue(purchaseDate.getFullYear());
      }

      if (_.isNil(this.security.caseSecurityRequest) === false) {
        this.tad_additional_security_property_value.
          setValue(this.security._propertyValue);
        this.tad_additional_security_property_category.
          setValue(_.isNil(this.security.caseSecurityRequest.
            CaseSecurityAttributeRequest[0].PropertyCategoryId) ? null :
            _.toString(this.security.caseSecurityRequest.
              CaseSecurityAttributeRequest[0].PropertyCategoryId));
        this.tad_additional_security_property_type.setValue(
          this.security.caseSecurityRequest.CaseSecurityAttributeRequest[0].PropertyTypeId);
      }

      if (_.isNil(this.security.existingChargeDetailRequest) === false &&
        _.isEmpty(this.security.existingChargeDetailRequest) === false) {

        this.tad_additional_security_secured.
          setValue(this.api.isNew(this.security.existingChargeDetailRequest[0].Id) === false ?
            _.isNil(this.security.existingChargeDetailRequest[0].Balance) ? 'false' :
              _.toString(this.security.existingChargeDetailRequest[0].Balance > 0) : null);
        if (!_.isNil(this.security.existingChargeDetailRequest[1])) {
          this.tad_additional_security_secured_outstanding_mortgage_other_loans_secured.
            setValue(this.api.isNew(this.security.existingChargeDetailRequest[1].Id) === false ?
              _.isNil(this.security.existingChargeDetailRequest[1].Balance) ? 'false' :
                _.toString(this.security.existingChargeDetailRequest[1].Balance > 0) : null);
        } else {
          this.tad_additional_security_secured_outstanding_mortgage_other_loans_secured.
            setValue('false');
        }
        this.tad_additional_security_secured_outstanding_mortgage_balance.
          setValue(this.security._firstChargeValue);
        this.tad_additional_security_secured_outstanding_mortgage_lender.
          setValue(this.security._firstChargeLender);
        this.tad_additional_security_secured_outstanding_mortgage_loan_balance.
          setValue(this.security._secondChargeValue);
      } else {
        this.tad_additional_security_secured.setValue('false');
      }

      this.prepareForm();
    }
  }

  /**
   *
   *
   * @memberof TogetherAuctionDIPAdditionalDetailsSecurityComponent
   */
  prepareToggles() {

    this.prepareForm();
  }


  /**
   *
   * @param {string} control
   * @returns
   * @memberof TogetherAuctionDIPApplicantDetailComponent
   */
  setDynamicClasses(control: string) {
    return {
      'is-invalid form-control-danger': this.additionalSecurityForm.get(control).touched
        && this.additionalSecurityForm.get(control).invalid,
      'is-valid form-control-success': this.additionalSecurityForm.get(control).touched
        && this.additionalSecurityForm.get(control).valid,
    };
  }

  /**
   *
   * @param {string} control
   * @returns
   * @memberof TogetherAuctionDIPApplicantDetailComponent
   */
  setDynamicClassesForFormgroup(control: string) {
    return {
      'is-invalid': this.additionalSecurityForm.get(control).touched
        && this.additionalSecurityForm.get(control).invalid,
      'is-valid': this.additionalSecurityForm.get(control).touched
        && this.additionalSecurityForm.get(control).valid,
    };
  }


  /**
   *
   *
   * @returns
   * @memberof TogetherAuctionDIPAdditionalDetailsSecurityComponent
   */
  validateFields() {
    return (group: UntypedFormGroup): { [key: string]: any } => {

      const month = this.tad_additional_security_purchase_date_month;
      const year = this.tad_additional_security_purchase_date_year;

      //  _day = day.value,
      const monthValue = month.value;
      const yearValue = year.value;

      const date = moment([yearValue, monthValue - 1, 1]);
      const currentDate = moment([]);
      const dateOldest = moment([1000, 10, 1]);

      const errors = {};

      if (!month.dirty || !year.dirty) {
        return null;
      }
      if (date.isValid() === false) {
        errors['dateValueInvalid'] = true;
      }
      if (date.isBefore(dateOldest)) {
        errors['olderThanGregorian'] = true;
      }
      if (date.isAfter(currentDate)) {
        errors['tooFarInFuture'] = true;
      }

      if (_.isEmpty(errors)) {
        return null;
      }
      return errors;
    };
  }

  //
  onChanges() {
    this.additionalSecurityForm.get('tad_additional_security_secured').valueChanges.
      subscribe((val) => {
        if (val === 'true') {
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_balance').enable();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_balance').
            setValidators([Validators.required, Validators.min(0)]);
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_lender').enable();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_lender').
            setValidators([Validators.required, Validators.min(0)]);
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_other_loans_secured').
            enable();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_other_loans_secured').
            setValidators([Validators.required]);
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_other_loans_secured').
            markAsUntouched({ onlySelf: true });
        } else if (val === 'false') {
          this.deleteExistingCharge(1);
          this.deleteExistingCharge(0);
          this.security.existingChargeDetailRequest = [];
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_balance').
            clearValidators();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_balance').reset();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_balance').disable();

          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_lender').
            clearValidators();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_lender').reset();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_lender').disable();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_other_loans_secured')
            .disable();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_other_loans_secured')
            .clearValidators();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_loan_balance').
            clearValidators();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_loan_balance').reset();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_loan_balance').disable();

        }
      });

    this.additionalSecurityForm.
      get('tad_additional_security_secured_outstanding_mortgage_other_loans_secured').
      valueChanges.subscribe((val) => {
        if (val === 'true') {
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_loan_balance').enable();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_loan_balance').
            setValidators([Validators.required, Validators.min(0)]);
        } else if (val === 'false') {
          this.deleteExistingCharge(1);
          this.security.existingChargeDetailRequest.splice(1, 1);
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_loan_balance').
            clearValidators();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_loan_balance').reset();
          this.additionalSecurityForm.
            get('tad_additional_security_secured_outstanding_mortgage_loan_balance').disable();
        }
      });
  }

  /**
   *
   *
   * @memberof TogetherAuctionDIPAdditionalDetailsSecurityComponent
   */
  ngOnInit() {
    this.handleInit();
    this.parentFormGroup.setControl('additionalSecurityForm', this.additionalSecurityForm);
    this.formChanges();
    this.onChanges();
  }

  formChanges() {
    this.additionalSecurityForm.statusChanges.subscribe((form) => {
      this.form_out.emit(this.additionalSecurityForm);
    });
  }

  /**
   *
   *
   * @param {SimpleChanges} changes
   * @memberof TogetherAuctionDIPAdditionalDetailsSecurityComponent
   */
  ngOnChanges(changes: SimpleChanges) {
    if (!_.isNil(changes.do_save)) {
      if (changes.do_save.previousValue === false && changes.do_save.currentValue === true) {
        this.doSaveSecurity();
      }
    }
  }


  /**
  *
  *
  * @memberof TogetherAuctionDIPAdditionalDetailsComponent
  */
  deleteExistingCharge(iter: number) {
    if (!_.isNil(this.security.caseSecurityRequest) &&
      !_.isEmpty(this.security.existingChargeDetailRequest) &&
      !_.isNil(this.security.existingChargeDetailRequest[iter]) &&
      this.api.isNew(this.security.existingChargeDetailRequest[iter].Id) === false) {
      this._isLoading = true;
      this.ref.detach();
      this.entity.deleteEntities(TogetherAuctionDIPEntityType.ExistingCharge,
        this.incoming_case_id,
        this.security.caseSecurityRequest.Id,
        this.security.existingChargeDetailRequest[iter].Id)
        .subscribe(
          (success) => {
            try {
              this._isLoading = false;
              console.log(iter);
              this.ref.reattach();
              // tslint:disable-next-line:variable-name
            } catch (JSONParseError) {

            }
          },
          (error) => {
            return;
          },
        );
      // const type = iter + 1;
      // this.security.existingChargeDetailRequest =
      //             this.security.existingChargeDetailRequest.splice(iter, 1);
      console.dir(this.security.existingChargeDetailRequest);
    }
  }


  /**
  * Handles the address returned by the address lookup component.
  *
  * @param {any} address
  * @memberof TogetherAuctionDIPApplicantCompanyComponent
  */
  _handleIncomingAddress(address) {
    this.address = address.address;
  }

  /**
   *
   *
   * @memberof TogetherAuctionDIPAdditionalDetailsSecurityComponent
   */
  ngAfterContentChecked() {
    this.prepareSecurity();
    this.prepareToggles();
    this.form_out.emit(this.additionalSecurityForm);
  }

  /**
   *
   *
   * @memberof TogetherAuctionDIPAdditionalDetailsSecurityComponent
   */
  prepareForm() {
    this.additionalSecurityForm = this.fb.group({
      tad_additional_security_address_postcode: this.tad_additional_security_address_postcode,
      tad_additional_security_address_house_number_name:
        this.tad_additional_security_address_house_number_name,
      tad_additional_security_address_street1: this.tad_additional_security_address_street1,
      tad_additional_security_address_town: this.tad_additional_security_address_town,
      tad_additional_security_address_city: this.tad_additional_security_address_city,
      tad_additional_security_address_county: this.tad_additional_security_address_county,
      tad_additional_security_property_category:
        this.tad_additional_security_property_category,
      tad_additional_security_property_type: this.tad_additional_security_property_type,
      tad_additional_security_property_value: this.tad_additional_security_property_value,
      tad_additional_security_purchase_date_month:
        this.tad_additional_security_purchase_date_month,
      tad_additional_security_purchase_date_year:
        this.tad_additional_security_purchase_date_year,
      tad_additional_security_purchase_price: this.tad_additional_security_purchase_price,
      tad_additional_security_secured: this.tad_additional_security_secured,
      tad_additional_security_secured_outstanding_mortgage_balance:
        this.tad_additional_security_secured_outstanding_mortgage_balance,
      tad_additional_security_secured_outstanding_mortgage_lender:
        this.tad_additional_security_secured_outstanding_mortgage_lender,
      tad_additional_security_secured_outstanding_mortgage_charge_type:
        this.tad_additional_security_secured_outstanding_mortgage_charge_type,
      tad_additional_security_secured_outstanding_mortgage_other_loans_secured:
        this.tad_additional_security_secured_outstanding_mortgage_other_loans_secured,
      tad_additional_security_secured_outstanding_mortgage_loan_balance:
        this.tad_additional_security_secured_outstanding_mortgage_loan_balance,
      tad_additional_security_address: this.tad_additional_security_address,
      addresslookup: this.addresslookup,
    }, { validator: this.validateFields() });
  }

  constructor(private api: NodeAPIService,
    private ref: ChangeDetectorRef,
    private entity: NodeAPIEntityService,
    private fb: UntypedFormBuilder,
    private exception: ExceptionService) {
    this.prepareForm();
  }
}
